import { Invoice, InvoiceStatus } from '../../../clients/quipli-service/invoice/invoice';
import { useEffect, useState } from 'react';
import { usePaymentApiV2 } from '../../../clients/quipli-service/payment/payment-api-v2';
import { toast } from '../../../util/toasts';
import {
  Payment,
  PaymentMethodType,
  PaymentRefundRequirement,
  PaymentStatus,
  RefundReason,
} from '../../../clients/quipli-service/payment/payment';
import { FiltersModal } from '../../../components/modal/filters-modal';
import { Typography } from '../../../components/typography/typography';
import { displayMoneyFormat } from '../../../util/util';
import classNames from 'classnames';
import { components } from 'react-select';
import { SelectForm } from '../../../components/select-form/select-form';
import { useOrderApiV1 } from '../../../clients/quipli-service/order/order-api-v1';
import { Order } from '../../../clients/quipli-service/order/order';
import _ from 'lodash';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { WorkOrder } from '../../../clients/quipli-service/service-and-repair/service-and-repair';
import { ButtonGroup } from '../../../components/button-group/button-group';
import { TerminalSelect } from '../../payment/stripe/terminal-component/terminal-select';

type PaymentMethodToRefund = {
  methodType: string;
  detail?: string;
  amount: number;
};

export const RefundPaymentModal = ({
  invoice,
  isOpen,
  close,
  currency,
}: {
  isOpen: boolean;
  close: (payments?: Payment[]) => void;
  invoice: Invoice;
  currency: string;
}) => {
  const { refundInvoice, unapplyPayment, refundRequirements } = usePaymentApiV2();
  const { getOrderById } = useOrderApiV1();
  const { getWorkOrderById } = useAPI();
  const [order, setOrder] = useState<Order | WorkOrder>();
  const [refundFromOptions, setRefundFromOptions] = useState<PaymentMethodToRefund[]>([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethodToRefund | undefined>();
  const [action, setAction] = useState<'refund' | 'unapply'>('refund');
  const [requireTerminal, setRequireTerminal] = useState<boolean>(false);
  const [facilitate, setFacilitate] = useState<boolean>(false);

  useEffect(() => {
    if (invoice.type === 'Order') {
      getOrderById(parseInt(invoice.typeId))
        .then(setOrder)
        .catch((e: any) => {
          console.log(e);
          toast(`Failed to load order`, 'error');
        });
    } else if (invoice.type === 'WorkOrder') {
      getWorkOrderById(invoice.typeId)
        .then(setOrder)
        .catch((e: any) => {
          console.log(e);
          toast(`Failed to load work order`, 'error');
        });
    }
  }, []);

  useEffect(() => {
    if (order && order.invoices) {
      const methodTotal: Record<
        string,
        {
          methodType: string;
          amount: number;
        }
      > = {};
      order.invoices
        .flatMap((inv) => inv.payments)
        .forEach((p) => {
          if (p.status !== PaymentStatus.succeeded || p.method == 'Credit') return;

          let method: string;

          switch (p.method) {
            case PaymentMethodType.Stripe: {
              method = p.methodDetail || p.method;
              break;
            }
            default: {
              method = p.method;
              break;
            }
          }

          if (methodTotal[method] !== undefined) {
            methodTotal[method].amount += p.amount;
          } else {
            methodTotal[method] = {
              methodType: p.method,
              amount: p.amount,
            };
          }
        });
      setRefundFromOptions(
        Object.entries(methodTotal)
          .filter(([record, { amount }]) => amount > 0 && record !== 'QBO')
          .map(([record, { methodType, amount }]) => {
            let detail: string | undefined;
            if (methodType === 'Stripe') {
              detail = record;
            }

            return {
              methodType,
              amount,
              detail,
            };
          }),
      );
    }
  }, [order]);

  useEffect(() => {
    if (!selectedPaymentMethod) {
      const defaultMethod = refundFromOptions.length
        ? refundFromOptions.sort((a, b) => b.amount - a.amount)[0]
        : undefined;
      if (defaultMethod) {
        setSelectedPaymentMethod(defaultMethod);
      }
    } else if (invoice.status === InvoiceStatus.Void) {
      setAmount(selectedPaymentMethod.amount);
    }
  }, [refundFromOptions, selectedPaymentMethod]);

  const refundReasonsOptions = Object.values(RefundReason).map((r) => {
    return { id: r, label: r };
  });
  const maxRefund = Number(
    (invoice.status === InvoiceStatus.Void
      ? selectedPaymentMethod
        ? selectedPaymentMethod.amount
        : _.sum(invoice.payments.map((p) => p.amount))
      : Math.min(
          selectedPaymentMethod && selectedPaymentMethod.methodType !== PaymentMethodType.Other
            ? selectedPaymentMethod.amount
            : -invoice.balance,
          -invoice.balance,
        )
    ).toFixed(2),
  );
  const [amount, setAmount] = useState<number>(invoice.status === InvoiceStatus.Void ? maxRefund : -invoice.balance);
  const [selectedReason, setSelectedReason] = useState<string>(refundReasonsOptions[0].id);
  const [isCapturing, setIsCapturing] = useState<boolean>(false);
  const formattedAmount = displayMoneyFormat(amount, currency);

  async function doRefund() {
    if (amount && selectedReason && selectedPaymentMethod) {
      setIsCapturing(true);
      try {
        if (action == 'refund') {
          const request = {
            amount: amount,
            reason: Object.keys(RefundReason)[
              Object.values(RefundReason).indexOf(
                refundReasonsOptions.find((r) => r.id === selectedReason)?.id || RefundReason.Other,
              )
            ] as RefundReason,
            method:
              (Object.keys(PaymentMethodType).find(
                (m) => selectedPaymentMethod.methodType == m.toString(),
              ) as PaymentMethodType) || PaymentMethodType.Other,
            detail: selectedPaymentMethod.detail,
          };
          if (requireTerminal) {
            setFacilitate(true);
          }
          const requirements = await refundRequirements(invoice.id, request);
          if (!requirements.length) {
            const result = await refundInvoice(invoice.id, request);

            toast('Refund Successful');
            close(result);
          } else if (requirements.includes(PaymentRefundRequirement.InPerson)) {
            setRequireTerminal(true);
          }
        } else {
          const result = await unapplyPayment(invoice.id, {
            amount: amount,
            reason: refundReasonsOptions.find((r) => r.id === selectedReason)?.id || RefundReason.Other,
            method:
              (Object.keys(PaymentMethodType).find(
                (m) => selectedPaymentMethod.methodType == m.toString(),
              ) as PaymentMethodType) || PaymentMethodType.Other,
            detail: selectedPaymentMethod.detail,
          });
          toast('Successfully Unapplied Payment');
          close(result);
        }
      } catch (ex: any) {
        toast('Error refunding invoice', 'error');
      } finally {
        setIsCapturing(false);
      }
    }
  }
  const buttonItems: { label: string; value: 'refund' | 'unapply' }[] = [
    { label: 'Refund', value: 'refund' },
    { label: 'Unapply', value: 'unapply' },
  ];

  return (
    <FiltersModal
      isOpen={isOpen}
      title={`Invoice #${invoice.invoiceNumber}`}
      confirmText={`${action == 'refund' ? 'Refund' : 'Unapply'} ${formattedAmount}`}
      cancelText="Cancel"
      allowConfirm={!isCapturing && maxRefund >= amount && amount > 0 && !!selectedPaymentMethod}
      allowCancel={!isCapturing}
      showConfirmButtonFirst={false}
      onConfirm={() => {
        doRefund();
      }}
      onCancel={() => {
        close();
      }}
    >
      {!requireTerminal ? (
        <>
          <div className="min-w-full mb-[24px] border border-solid border-[var(--grey-200)] rounded-lg flex flex-col gap-2">
            <div className="m-[8px] mb-[0] flex items-center justify-between">
              <Typography type="text-sm" weight="regular" className="text-[var(--grey-800)]">
                Due Date
              </Typography>
              <Typography type="text-sm" weight="regular" className="text-[var(--grey-800)]">
                {invoice.dueDate?.toFormat('LLL-dd t') || 'N/A'}
              </Typography>
            </div>
            <div className="m-[8px] flex items-center justify-between">
              <Typography type="text-sm" weight="medium" className="text-[var(--grey-800)]">
                Pending Refund
              </Typography>
              <Typography type="text-sm" weight="medium" className="text-[var(--grey-800)]">
                {displayMoneyFormat(
                  invoice.status === InvoiceStatus.Void
                    ? _.sum(invoice.payments.map((p) => p.amount))
                    : invoice.balance,
                  currency,
                )}
              </Typography>
            </div>
          </div>
          <div className="m-[8px] flex justify-center items-center" id={'refund-group'}>
            <ButtonGroup<'refund' | 'unapply'>
              items={buttonItems}
              onClick={(value) => setAction(value as 'refund' | 'unapply')}
              selected={action}
            />
          </div>
          <div className="min-w-full flex flex-col gap-2 items-stretch">
            <Typography type="text-xl">{action == 'refund' ? 'Issue Refund' : ''}</Typography>
            <div className="min-w-full h-[44px] p-[10px] border border-solid border-[var(--grey-300)] rounded-lg flex-[0_1_100%] flex items-center">
              <span className="ml-1 quipli-v2 text-md flex-[0_1_5%] ">$</span>
              <input
                type="number"
                step="0.01"
                className="appearance-textfield !outline-none !border-0 !border-none quipli-v2 text-md flex-[0_1_95%]"
                value={amount}
                onChange={(e) => setAmount(+e.target.value)}
              />
            </div>
            {maxRefund < amount && (
              <Typography type="text-sm" weight="regular" className="text-[var(--error-500)]">
                Refund amount cannot exceed {displayMoneyFormat(maxRefund, currency)}
              </Typography>
            )}

            <Typography type="text-md" weight="medium" className="text-[var(--grey-700)] mt-[16px]">
              {action == 'refund' ? 'Refund From' : 'Unapply Payment From Invoice'}
            </Typography>
            {action == 'unapply' && (
              <Typography type="text-sm" weight="regular" className="text-[var(--grey-700)] mt-[16px]">
                Unapplied payments will appear in the customer collectibles
              </Typography>
            )}
            <SelectForm
              options={refundFromOptions}
              value={selectedPaymentMethod}
              isSearchable={false}
              getOptionLabel={({ methodType, detail, amount }) =>
                `${detail || methodType} (${displayMoneyFormat(amount, currency)})`
              }
              getOptionValue={(op) => `${op.methodType}`}
              isDisabled={refundFromOptions.length < 1}
              className={classNames('w-[380px] h-[44px]')}
              onChange={(op) => setSelectedPaymentMethod(op as PaymentMethodToRefund)}
              components={{
                SingleValue: (props) => (
                  <components.SingleValue {...props}>
                    <Typography type="text-md" className="text-[var(--grey-800)]">
                      {props.children}
                    </Typography>
                  </components.SingleValue>
                ),
              }}
            />

            <Typography type="text-md" weight="medium" className="text-[var(--grey-700)] mt-[16px]">
              Reason
            </Typography>
            <SelectForm
              options={refundReasonsOptions}
              value={refundReasonsOptions.find((r) => r.id === selectedReason)}
              isSearchable={false}
              className={classNames('w-[380px] h-[44px]')}
              getOptionLabel={(r) => r.label}
              onChange={(r) => setSelectedReason(r?.id as string)}
              components={{
                SingleValue: (props) => (
                  <components.SingleValue {...props}>
                    <Typography type="text-md" className="text-[var(--grey-800)]">
                      {props.children}
                    </Typography>
                  </components.SingleValue>
                ),
              }}
            />
          </div>
        </>
      ) : (
        <TerminalSelect
          user={invoice.customer}
          locationId={invoice.locationId}
          setCardId={() => {}}
          close={close}
          onComplete={close}
          onCancel={close}
          showFacilitator={facilitate}
          setMethod={() => {}}
          method={'refund'}
          refundRequest={{
            amount: amount,
            reason: selectedReason as RefundReason,
            method: PaymentMethodType.Stripe,
            detail: selectedPaymentMethod?.detail,
          }}
          invoiceId={invoice.id}
        ></TerminalSelect>
      )}
    </FiltersModal>
  );
};
